import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BASE_URL } from '../../utils/BASE_URL';
import axios from 'axios';

const initialState = {
    user: null,
    isAuthenticated: !!localStorage.getItem('token'),
    loading: false,
    error: null,
    loggedIn: false,
    isLoggedOut: false,
    message: null
};
const handleErrors = (error, dispatch, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || 'An error occurred';

    return rejectWithValue(error.response?.data || { message: errorMessage });
};
export const login = createAsyncThunk(
    'auth/login',
    async (data, { rejectWithValue }) => {
        try {
            try {
                const response = await axios.post(`${BASE_URL}/login`, data);

                localStorage.setItem('token', response.data.token);
                localStorage.setItem('userId', response.data.user._id);
                localStorage.setItem('role', response.data.user.role);
                return response.data;
            } catch (error) {
                return handleErrors(error, null, rejectWithValue);
            }
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const googleLogin = createAsyncThunk(
    "auth/google-login",
    async ({ uid, firstName, lastName, email, role }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${BASE_URL}/google-login`,
                {
                    uid,
                    firstName,
                    lastName,
                    email,
                    role
                },
                { withCredentials: true }
            );
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("userId", response.data.user._id);
            localStorage.setItem("role", response.data.user.role);
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const facebookLogin = createAsyncThunk(
    "auth/facebook-login",
    async ({ uid, firstName, lastName, email, role, picture }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${BASE_URL}/facebook-login`,
                {
                    uid,
                    firstName,
                    lastName,
                    email,
                    role,
                    picture
                },
                { withCredentials: true }
            );
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("role", response.data.user.role);
            localStorage.setItem("userId", response.data.user._id);
            return response.data;
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const register = createAsyncThunk(
    'auth/register',
    async (userData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/register`, userData);
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userId', response.data.user._id);
            localStorage.setItem('role', response.data.user.role);
            return response.data;
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const forgotPassword = createAsyncThunk(
    'auth/forgotPassword',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/forgot-password`, data);
            if (response.status === 200) {
                return response.data; // Assuming the API returns a success message
            }
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const verifyOtp = createAsyncThunk(
    'auth/verifyOtp',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/verify`, data);
            return response.data;
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/change-password`, data);
            return response.data;
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const resendOtp = createAsyncThunk(
    'auth/resendOtp',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/resend-otp`, data);
            return response.data;
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const adminSignupOtpVerify = createAsyncThunk(
    'auth/adminSignupOtpVerify',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/admin-signup-otp`, data, { withCredentials: true });
            if (response.data.token) {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('userId', response.data.user._id);
                localStorage.setItem('role', response.data.user.role);
            }
            return response.data;
        } catch (error) {
            return handleErrors(error, null, rejectWithValue);
        }
    }
);

export const logoutUser = createAsyncThunk(
    "auth/logoutUser",
    async (userId, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/logout/${userId}`);
            if (response.status === 200) {
                localStorage.removeItem("userId");
                localStorage.removeItem("role");
                localStorage.removeItem("token");
                if (window.persistor) {
                    window.persistor.purge();
                }
                return response.data;
            }
        } catch (error) {
            return handleErrors(error, dispatch, rejectWithValue);
        }
    }
);

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        updateUserRole: (state, action) => {
            state.user.role = action.payload;
            localStorage.setItem('role', action.payload);
        }
    },

    extraReducers: (builder) => {
        builder

            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.user = action.payload?.user || null;
                state.isAuthenticated = true;
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Login successfully";
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.error = action.payload?.message || "Login Failed";
                state.message = action.payload?.message || "Login Failed";
            })
            .addCase(googleLogin.fulfilled, (state, action) => {
                if (action.payload && action.payload.user) {
                    state.user = action.payload.user;
                    state.isAuthenticated = true;
                    state.loading = false;
                    state.error = null;
                    state.message = action.payload?.message || "Google Login successful";
                }
            })
            .addCase(googleLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
                state.message = action.payload?.message || "Google Login Failed";
            })
            .addCase(facebookLogin.fulfilled, (state, action) => {
                if (action.payload && action.payload.user) {
                    state.user = action.payload.user;
                    state.isAuthenticated = true;
                    state.loading = false;
                    state.error = null;
                    state.message = action.payload?.message || "Facebook Login successful";
                }
            })
            .addCase(facebookLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload.message;
                state.message = action.payload?.message || "Facebook Login Failed";
            })
            .addCase(register.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(register.fulfilled, (state, action) => {
                state.user = action.payload?.user || null;
                state.isAuthenticated = true;
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Register successfully";
            })
            .addCase(register.rejected, (state, action) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.error = action.payload?.message || "User Already Exist";
                state.message = action.payload?.message || "User Already Exist";
            })
            .addCase(forgotPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || action.payload?.message || "Email Sent Successfully...";
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Forgot Password Failed";
                state.message = action.payload?.message || "Forgot Password Failed";
            })
            .addCase(verifyOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(verifyOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Otp Verify SuccessFully...";
            })
            .addCase(verifyOtp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Verify OTP Failed";
                state.message = action.payload?.message || "Verify OTP Failed";
            })
            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "Password Changed SuccessFully...";
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Reset Password Failed";
                state.message = action.payload?.message || "Reset Password Failed";
            })
            .addCase(resendOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(resendOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "OTP Resent Successfully...";
            })
            .addCase(resendOtp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Resend OTP Failed";
                state.message = action.payload?.message || "Resend OTP Failed";
            })
            .addCase(logoutUser.fulfilled, (state, action) => {
                state.user = null;
                state.isAuthenticated = false;
                state.loggedIn = false;
                state.isLoggedOut = true;
                state.message = action.payload?.message || "Logged out successfully";
            })
            .addCase(logoutUser.rejected, (state, action) => {
                state.error = action.payload.message;
                state.message = action.payload?.message || "Logout Failed";
            })
            .addCase(adminSignupOtpVerify.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(adminSignupOtpVerify.fulfilled, (state, action) => {
                state.user = action.payload?.user || null;
                state.isAuthenticated = true;
                state.loading = false;
                state.error = null;
                state.message = action.payload?.message || "OTP verified successfully";
            })
            .addCase(adminSignupOtpVerify.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "OTP verification failed";
                state.message = action.payload?.message || "OTP verification failed";
            });

    }

});

export const { updateUserRole } = authSlice.actions;

export default authSlice.reducer;